import { createContext, useContext, useEffect, useState } from "react";
import { TUserInfo, TUserTypes } from "../lib/types/users";
import { Footer } from "../components/sections/main-page/footer";
import { getFirebaseAppAuth } from "../services/users";
import { ChatOpenChatList } from "../components/chat/chat";
import { useServiceContext } from "./service";
import { Header } from "../components/common/header";
import { BlockedUserNotice } from "../components/common/block-notice";
import { evaluators, useWindowSizeMatch } from "../lib/window-size-matcher";
import { DEVICES_SIZES_IN_PX } from "../lib/common/constants";
import { useInactivityLogout } from "./inactivity-logout";

type TSecureSectionProps = {
    children: any;
    type?: TUserTypes;
    allowUserEnabled?: boolean;
    types?: TUserTypes[];
};

type AuthInformation = {
    isLoggedIn: boolean;
    isUserEmailVerified: () => Promise<boolean>;
    setIsLoggedIn: (_val: boolean) => void;
    getUserTokenId: () => Promise<string>;
    setUserTokenId: (_val: string) => void;
    logInUser: (_tokenId: string, _userInfo: TUserInfo) => void;
    logOutUser: () => void;
    setUserInfo: (_v: TUserInfo) => void;
    SecureSection: (_p: TSecureSectionProps) => React.ReactElement;
    updateUserInfoInStorage: (_info: TUserInfo) => void;
    updateUserInfoFromLocalStorage: () => void;
    userInfo?: TUserInfo;
    refreshFBTokenId: () => Promise<void>;
};

const AuthContext = createContext<AuthInformation>({
    isLoggedIn: false,
    isUserEmailVerified: async () => false,
    setIsLoggedIn: (_v: boolean) => {},
    getUserTokenId: async () => "",
    setUserTokenId: (_v: string) => {},
    logOutUser: () => {},
    setUserInfo: () => {},
    SecureSection: (_p: TSecureSectionProps) => <></>,
    logInUser: (_tokenId: string, _userInfo: TUserInfo) => {},
    updateUserInfoInStorage: (_info) => {},
    updateUserInfoFromLocalStorage: () => {},
    refreshFBTokenId: async () => {},
});

export const useAuthContext = () => {
    return useContext(AuthContext);
};

export const AuthProvider = (props: any) => {
    const { updateLastActivity, sessionHasExpired, clearLastActivity } = useInactivityLogout();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userTokenId, setUserTokenIdState] = useState("");
    let lastTimeTokenId: number | undefined = undefined;
    const [userInfo, setUserInfo] = useState<TUserInfo>({
        id: -1,
        guid: "",
        name: "",
        other: undefined,
        type: "lab",
        enabled: true,
        images: {
            background: "",
            external: "",
            internal: "",
            logo: "",
            resume: "",
        },
        suprSend: {
            userToken: "",
        },
        incognito: false,
        verifications: {
            phone: false,
            email: undefined,
        },
    });
    const {
        chats: { removeSocket },
    } = useServiceContext();

    const cleanUp = () => {
        setUserTokenId("");
        setUserInfo({
            id: -1,
            guid: "",
            name: "",
            other: undefined,
            type: "lab",
            enabled: true,
            images: {
                background: "",
                external: "",
                internal: "",
                logo: "",
                resume: "",
            },
            suprSend: {
                userToken: "",
            },
            incognito: false,
            verifications: {
                phone: false,
                email: undefined,
            },
        });
        updateUserInfoInStorage(undefined);
        localStorage.setItem("tokenId", "");
        localStorage.removeItem("ss_distinct_id");
        clearLastActivity();
        removeSocket();
    };

    const isUserEmailVerified = async (): Promise<boolean> => {
        const _fbApp = getFirebaseAppAuth();
        if (!_fbApp.currentUser) {
            await delay(1000);
            return isUserEmailVerified();
        }
        return _fbApp.currentUser?.emailVerified || false;
    };
    const logOutUser = () => {
        cleanUp();
        setIsLoggedIn(false);
    };

    const updateUserInfoInStorage = (info?: TUserInfo) => {
        localStorage.setItem("userInfo", info ? JSON.stringify(info) : "");
    };

    const logInUser = (tokenId: string, userInfo?: TUserInfo) => {
        setIsLoggedIn(true);
        updateLastActivity();
        if (userInfo) {
            setUserInfo(userInfo);
            updateUserInfoInStorage(userInfo);
        }
        setUserTokenId(tokenId);
        localStorage.setItem("tokenId", tokenId);
    };

    const SecureSection = ({ children, type = "other", types, allowUserEnabled }: TSecureSectionProps): React.ReactElement => {
        const { conditionFulfilled: isSmallDevice } = useWindowSizeMatch({
            condition: evaluators.lessThan(DEVICES_SIZES_IN_PX.medium),
        });
        let isUserTypeAllowed = false;
        if (type) {
            isUserTypeAllowed = type !== "other" ? userInfo.type === type : true;
        } else if (types && types.length > 0) {
            isUserTypeAllowed = types.includes(userInfo.type);
        }

        if (!type && (!types || types.length === 0)) throw new Error("An user type must be indicated");

        //        const isUserTypeAllowed = type !== "other" ? userInfo.type === type : true;
        const isUserEnabled = allowUserEnabled !== undefined ? allowUserEnabled : userInfo.enabled;

        if (!!!isLoggedIn) {
            return (
                <>
                    <Header title="Unauthorized Access" />
                    <div className="container text-center mt-5" style={{ minHeight: "10rem" }}>
                        <p>You need to be logged in to access this section.</p>
                    </div>
                    <Footer />
                </>
            );
        }

        if (!isLoggedIn || !isUserTypeAllowed) {
            return (
                <>
                    <Header title="" />
                    <div className="container text-center mt-5" style={{ minHeight: "10rem" }}>
                        <p>You're not authorized to be here</p>
                    </div>
                    <Footer />
                </>
            );
        } else if (!!!isUserEnabled) {
            return (
                <>
                    <Header title="" />
                    <div className="container text-center mt-5" style={{ minHeight: "10rem" }}>
                        <BlockedUserNotice />
                    </div>
                    <Footer />
                </>
            );
        } else {
            return (
                <>
                    {children}
                    {!isSmallDevice.fulfilled && userInfo.type !== "admin" && <ChatOpenChatList />}
                </>
            );
        }
    };

    const setUserTokenId = (tokenId: string) => {
        lastTimeTokenId = Date.now();
        setUserTokenIdState(tokenId);
    };

    function delay(milliseconds: number) {
        return new Promise((resolve) => {
            setTimeout(resolve, milliseconds);
        });
    }

    /**
     * Returns the current Firebase ID token used for authenticated API requests.
     *
     * @param forceGetToken - When true, forces a refresh of the Firebase ID token
     * even if the cached token is still valid.
     *
     * IMPORTANT:
     * This parameter should be avoided whenever possible.
     * It exists only to handle exceptional cases where Firebase claims
     * (e.g. email_verified) have changed and must be immediately propagated
     * to the backend.
     *
     * Normal flow should rely on token caching and automatic refresh.
     */
    const getUserTokenId = async (forceGetToken: boolean = false): Promise<string> => {
        const nowTime = Date.now();

        if (forceGetToken || !lastTimeTokenId || nowTime - lastTimeTokenId > 300000) {
            const frbAppAuth = getFirebaseAppAuth();

            if (!frbAppAuth.currentUser) {
                await delay(1000);
                return await getUserTokenId(forceGetToken);
            }
            const newToken = await frbAppAuth.currentUser.getIdToken(forceGetToken);
            if (!newToken) {
                await delay(1000);
                return await getUserTokenId(forceGetToken);
            }
            if (newToken) {
                logInUser(newToken, undefined as any);
                return newToken;
            }
        }
        return userTokenId;
    };

    const retrieveFromLocalStorage = () => {
        const userInfoLS = localStorage.getItem("userInfo");
        const tokenIdLS = localStorage.getItem("tokenId");

        if (!userInfoLS || !tokenIdLS) return;
        if (sessionHasExpired()) {
            cleanUp();
            setIsLoggedIn(false);
            return;
        }

        setIsLoggedIn(true);
        setUserTokenId(tokenIdLS);
        setUserInfo(JSON.parse(userInfoLS) as TUserInfo);
    };

    const updateUserInfoFromLocalStorage = () => {
        const userInfoLS = localStorage.getItem("userInfo");
        if (!userInfoLS) return;
        setUserInfo(JSON.parse(userInfoLS) as TUserInfo);
    };

    const refreshFBTokenId = async () => {
        const auth = getFirebaseAppAuth();
        if (!auth.currentUser) {
            await delay(500);
            return refreshFBTokenId();
        }
        await auth.currentUser.reload();
        await getUserTokenId(true);
    };

    useEffect(() => {
        setTimeout(() => {
            retrieveFromLocalStorage();
        }, 0);
        //eslint-disable-next-line
    }, []);

    const value = {
        isLoggedIn,
        isUserEmailVerified,
        getUserTokenId,
        setIsLoggedIn,
        userTokenId,
        setUserTokenId,
        logOutUser,
        userInfo,
        setUserInfo,
        SecureSection,
        logInUser,
        updateUserInfoInStorage,
        updateUserInfoFromLocalStorage,
        refreshFBTokenId,
    };

    return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
};
